/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const WayPointCtor = (typeof globalThis !== "undefined" && (globalThis.WayPointNode || globalThis.Object3D)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const WAYPOINT_PROPS = [
  "canBeSpawnPoint",
  "canBeOccupied",
  "canBeClicked",
  "willDisableMotion",
  "willDisableTeleporting",
  "snapToNavMesh",
  "willMaintainInitialOrientation",
  "hoverBoxPosition",
  "hoverBoxScale",
  "sitAnimation",
  "networkedId"
];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class WayPointEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    WAYPOINT_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);

          // Special handling for Vector3 properties
          if (prop === "hoverBoxPosition" || prop === "hoverBoxScale") {
            const vec3 = this.sourceObject[prop] && this.sourceObject[prop].isVector3
              ? this.sourceObject[prop]
              : new THREE.Vector3();
            try {
              if (Array.isArray(v)) {
                vec3.set(v[0], v[1], v[2]);
              } else if (typeof v === "object") {
                vec3.set(v.x, v.y, v.z);
              }
              this._values.set(prop, vec3); // Store the THREE.Vector3 object
              const originalDescriptor = this._originalDescriptors.get(prop);
              if (originalDescriptor && originalDescriptor.set) {
                originalDescriptor.set.call(this.sourceObject, vec3);
              } else {
                this.sourceObject[prop] = vec3;
              }
            } catch (e) {
              console.warn(`Failed to set Vector3 for ${prop}:`, e);
            }
            return;
          }

          // Default property setting
          const originalDescriptor = this._originalDescriptors.get(prop);
          if (originalDescriptor && originalDescriptor.set) {
            try {
              originalDescriptor.set.call(this.sourceObject, v);
            } catch (e) {
              console.warn(`Failed to call original setter for ${prop}:`, e);
            }
          } else {
            try {
              this.sourceObject[prop] = v;
            } catch (e) {
              // ignore
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class WayPointAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const COMPONENT = "waypoint";
    const NETWORKED = "networked";
    const pending = (this._pendingUpdates = {});

    const flush = () => {
      const el = this.sourceObject?.el;
      if (!el) return;

      if (pending.waypoint) {
        const current = { ...(el.getAttribute(COMPONENT) || {}) };
        Object.assign(current, pending.waypoint);
        el.setAttribute(COMPONENT, current);
        delete pending.waypoint;
      }
      if (pending.networked) {
        const current = { ...(el.getAttribute(NETWORKED) || {}) };
        Object.assign(current, pending.networked);
        el.setAttribute(NETWORKED, current);
        delete pending.networked;
      }
    };

    this.sourceObject?.el?.addEventListener("componentinitialized", e => {
      if (e?.detail?.name === COMPONENT || e?.detail?.name === NETWORKED) {
        flush();
      }
    });

    const setComponentProp = (componentName, key, val) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute(componentName);
      if (!attr) {
        if (!pending[componentName]) {
          pending[componentName] = {};
        }
        pending[componentName][key] = val;
      } else {
        const current = { ...attr };
        current[key] = val;
        el.setAttribute(componentName, current);
      }
    };

    const getComponentProp = (componentName, key) => {
      const el = this.sourceObject?.el;
      if (!el) return pending[componentName]?.[key];
      const attr = el.getAttribute(componentName);
      return (attr && attr[key]) ?? pending[componentName]?.[key];
    };

    WAYPOINT_PROPS.forEach(prop => {
      if (prop === "networkedId") return; // special case below
      Object.defineProperty(this, prop, {
        get: () => getComponentProp(COMPONENT, prop),
        set: v => setComponentProp(COMPONENT, prop, v)
      });
    });

    // Networked id - special handling
    Object.defineProperty(this, "networkedId", {
      get: () => getComponentProp(NETWORKED, "networkId"),
      set: v => {
        setComponentProp(NETWORKED, "networkId", v);
      }
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of WAYPOINT_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new WayPointAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new WayPointEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Main WayPointElement class                                                 */
/* ────────────────────────────────────────────────────────────────────────── */
export class WayPointElement extends ScriptableObject {
  static scriptableType = "waypoint";

  constructor(initial = {}) {
    super();

    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      el.setAttribute("waypoint", {
        canBeSpawnPoint: false,
        canBeOccupied: false,
        canBeClicked: false,
        willDisableMotion: false,
        willDisableTeleporting: false,
        snapToNavMesh: false,
        willMaintainInitialOrientation: false,
        hoverBoxPosition: { x: 0, y: 0.5, z: 0 },
        hoverBoxScale: { x: 1, y: 1, z: 1 },
        sitAnimation: "none",
        ...initial
      });

      const networkId = initial.networkedId || Math.random().toString(36).slice(2, 10);
      el.setAttribute("networked", {
        template: "#template-waypoint-avatar",
        attachTemplateToLocal: false,
        owner: "scene",
        persistent: true,
        networkId
      });

      const obj = this.constructor.adopt(el); // identity-preserving prototype shift
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy adapter + apply props (if any)
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }
      return obj;
    }

    // editor / plain Three
    if (!this.scriptableType) {
      Object.defineProperty(this, "scriptableType", { value: this.constructor.scriptableType, configurable: true });
    }

    const node = createWayPointElementEditor(initial);
    ensureAdapter(node);

    for (const k of Object.keys(initial)) {
      if (k in node) node[k] = initial[k];
    }

    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public waypoint props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      if (prop === "hoverBoxPosition" || prop === "hoverBoxScale") {
        if (val?.isVector3) {
          this[prop] = val.clone();
        } else if (Array.isArray(val)) {
          this[prop] = new THREE.Vector3(val[0], val[1], val[2]);
        } else if (typeof val === "object") {
          this[prop] = new THREE.Vector3(val.x, val.y, val.z);
        } else {
          this[prop] = val;
        }
      } else if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of WAYPOINT_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of source.children || []) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    // Build init payload so the new element is born with correct component state
    const init = {};
    for (const p of WAYPOINT_PROPS) {
      const v = this[p];
      if (v === undefined) continue;

      if (p === "hoverBoxPosition" || p === "hoverBoxScale") {
        if (v?.isVector3) {
          init[p] = v.clone();
        } else if (Array.isArray(v)) {
          init[p] = v.slice();
        } else if (typeof v === "object") {
          init[p] = { x: v.x, y: v.y, z: v.z };
        } else {
          init[p] = v;
        }
      } else if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    // Prefer factory if available (handles adopt/constructor nuances)
    const Ctor = this.constructor && typeof this.constructor.create === "function" ? this.constructor : WayPointElement;

    const dest = typeof Ctor.create === "function" ? Ctor.create(init) : new WayPointElement(init);

    // Write object3D transforms (component sync for A-Frame to be added later)
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    // Copy remaining fields & children
    return dest.copy(this, recursive);
  }

  static create(initial = {}) {
    return new this(initial);
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* DRY helper: upgrade a plain Editor WayPointNode instance to Scriptable    */
/* ────────────────────────────────────────────────────────────────────────── */
function makeEditorWayPointNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || WayPointCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: WayPointElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = WayPointElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new WayPointEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new WayPointEditorAdapter(node, descriptors);
  }

  for (const prop of WAYPOINT_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
WAYPOINT_PROPS.forEach(prop => {
  Object.defineProperty(WayPointElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createWayPointElementEditor(initial = {}) {
  const WayPointCtor = globalThis.WayPointNode;

  if (typeof WayPointCtor !== "function") {
    throw new Error("WayPointElementEditor: WayPointNode constructor not found.");
  }
  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new WayPointCtor(editor);
  } catch (e) {
    throw new Error(`WayPointElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorWayPointNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("waypoint", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== WayPointElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return WayPointElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorWayPointNodeScriptable(source);

  return source;
});

/* Optional: nicer DevTools label */
Object.defineProperty(WayPointElement.prototype, Symbol.toStringTag, { value: "WayPointElement" });

// Named exports expected by external consumers
export { WayPointAFrameAdapter, WayPointEditorAdapter };
